import { personMentionsIn } from './commentHtml';
import { extractMentionHandles } from './types';
import type { MentionPerson } from './commentMentions';

/**
 * A name that was typed at somebody and never became a mention.
 *
 * ── The failure this exists for ────────────────────────────────────────────
 *
 * A mention is an ELEMENT, not text. Choosing from the `@` picker inserts
 * `<mark class="comment-mention" data-id="…">`, and only that shape becomes a
 * comment_mentions row — which is what fires the notification. Type the whole
 * name and carry on without picking, and the letters stay letters: no row, no
 * notification, and no warning to the person who wrote it.
 *
 * That design is deliberate and it fixed a worse bug — the old composer matched
 * "@Name" as characters, so editing a name by hand left a notification aimed at
 * somebody the comment no longer mentioned. This module is the price of that
 * trade, paid at the one moment it can still be put right: before posting.
 *
 * ── Four ways to get here, all measured from the picker's own settings ─────
 *
 *   The picker needs TWO characters, so "@P" never opens it.
 *   The picker closes at a space, so "@Paul Rivera" typed in full loses it.
 *   Pasting inserts text, not nodes.
 *   Editing a saved comment and retyping a name by hand does the same.
 *
 * ── What this does NOT do ──────────────────────────────────────────────────
 *
 * It never rewrites anybody's comment. Kyu: "do not auto-rewrite what the user
 * typed." Converting text to a mention has to guess when two people share a
 * first name, and a composer that silently edits what you wrote is worse than
 * one that tells you what it noticed.
 */

/**
 * The body with every mention element removed, leaving only real text.
 *
 * Both shapes go: `<mark>` for a person and `<span data-type="mention">` for a
 * record. A record mention is not a person mention, but its label reads as
 * "@Something" and would otherwise be reported as a missed name.
 */
function textOutsideMentions(html: string): string {
  const holder = document.createElement('div');
  holder.innerHTML = html;
  for (const node of holder.querySelectorAll('mark.comment-mention, span[data-type="mention"]')) {
    node.remove();
  }
  // A space at every BLOCK boundary, then normalise.
  //
  // textContent glues blocks together: "<p>hello</p><p>@Ana</p>" reads as
  // "hello@Ana", and the handle rule needs a start-or-space before the @ — so
  // a name alone in its own paragraph would be invisible. Found by its own
  // test, not by reading.
  //
  // Block tags only, deliberately. Spacing every tag would split a word that
  // happens to carry inline formatting — "@Pa<strong>ul</strong>" would read
  // as "@Pa ul" — and turn a real missed mention into silence.
  holder.innerHTML = holder.innerHTML.replace(
    /<\/?(?:p|div|li|ul|ol|h[1-6]|blockquote|pre|br)\b[^>]*>/gi,
    ' $&',
  );
  return (holder.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** The words somebody could plausibly have typed to mean this person. */
function handlesFor(person: MentionPerson): string[] {
  const label = person.label.trim().toLowerCase();
  if (label === '') {
    return [];
  }
  const first = label.split(' ')[0] ?? '';
  // The full label first: "@Paul Rivera" typed in full yields the handle
  // "paul", because the extractor stops at the space — so the first word is
  // what actually has to match. The full label is kept for one-word names and
  // for a label that happens to contain no space at all.
  return first === '' || first === label ? [label] : [label, first];
}

/**
 * Names typed as plain text that match a real person and are not mentions.
 *
 * Returned as TYPED, not as the person's stored name, so the warning can quote
 * the writer's own words back at them rather than correcting their spelling.
 *
 * Only names matching a known active person are reported. "@here", "@2pm" and
 * "@4ocean" are not people and must not produce a warning — a notice that
 * cries wolf is one people learn to click past, which would cost more than the
 * bug. This is the same rule the counting query uses, so the warning and the
 * measurement can never disagree about what a missed mention is.
 */
export function missedMentionHandles(html: string, people: MentionPerson[]): string[] {
  if (people.length === 0) {
    return [];
  }
  const text = textOutsideMentions(html);
  const typed = extractMentionHandles(text);
  if (typed.length === 0) {
    return [];
  }

  // Already mentioned somewhere in this comment, so they ARE being notified.
  // Warning about a second, plain-text copy of the same name would be noise
  // about a message that is going to arrive.
  const mentioned = new Set(personMentionsIn(html).map((person) => person.id));

  const missed: string[] = [];
  for (const handle of typed) {
    const matches = people.filter((person) => handlesFor(person).includes(handle));
    if (matches.length === 0) {
      continue;
    }
    // Every person this could mean is already mentioned: nothing was lost.
    if (matches.every((person) => mentioned.has(person.id))) {
      continue;
    }
    missed.push(handle);
  }
  return missed;
}

/**
 * The warning, in words that say what to do rather than what went wrong.
 *
 * "@Paul is just text" is the finding; "pick them from the list" is the part
 * somebody can act on, and it is the half a notice usually leaves out.
 */
export function missedMentionNotice(handles: string[]): string {
  if (handles.length === 0) {
    return '';
  }
  const names = handles.map((handle) => `@${handle}`).join(', ');
  const isOne = handles.length === 1;
  return `${names} ${isOne ? 'is' : 'are'} just text, so ${
    isOne ? 'nobody is' : 'none of them are'
  } notified. Retype the name and pick ${
    isOne ? 'the person' : 'each person'
  } from the list to send it.`;
}
